import type ApexCharts from 'apexcharts'
import type { ApexOptions } from 'apexcharts'
import { consultarTablero, type IndicadoresTablero } from './cliente-tablero.js'

const elemento = <T extends Element>(selector: string): T => {
  const dato = document.querySelector<T>(selector)
  if (!dato) throw new Error(`No existe ${selector}`)
  return dato
}

let graficosActivos: ApexCharts[] = []

function poner(selector: string, valor: number): void {
  elemento<HTMLElement>(selector).textContent = valor.toLocaleString('es-CO')
}

function textoRol(rol: IndicadoresTablero['rolPrincipal']): Array<[string, string]> {
  switch (rol) {
    case 'COORDINADOR_ASESORES':
      return [
        ['#etiqueta-kpi-estudiantes', 'Estudiantes asignados'],
        ['#etiqueta-kpi-expedientes', 'Carpetas abiertas'],
        ['#etiqueta-kpi-pendientes', 'Carpetas con alertas'],
        ['#etiqueta-kpi-ejecucion', 'Asesores con alta carga'],
      ]
    case 'ASESOR':
      return [
        ['#etiqueta-kpi-estudiantes', 'Estudiantes asignados'],
        ['#etiqueta-kpi-expedientes', 'Carpetas con alertas'],
        ['#etiqueta-kpi-pendientes', 'Observaciones abiertas'],
        ['#etiqueta-kpi-ejecucion', 'Evaluaciones pendientes'],
      ]
    case 'TITULAR':
      return [
        ['#etiqueta-kpi-estudiantes', 'Estudiantes acompañados'],
        ['#etiqueta-kpi-expedientes', 'Apoyos por validar'],
        ['#etiqueta-kpi-pendientes', 'Evaluaciones pendientes'],
        ['#etiqueta-kpi-ejecucion', 'Observaciones abiertas'],
      ]
    case 'ESTUDIANTE':
      return [
        ['#etiqueta-kpi-estudiantes', 'Estado de la carpeta'],
        ['#etiqueta-kpi-expedientes', 'Módulos pendientes'],
        ['#etiqueta-kpi-pendientes', 'Observaciones por responder'],
        ['#etiqueta-kpi-ejecucion', 'Autoevaluaciones pendientes'],
      ]
    default:
      return [
        ['#etiqueta-kpi-estudiantes', 'Estudiantes activos'],
        ['#etiqueta-kpi-expedientes', 'Carpetas abiertas'],
        ['#etiqueta-kpi-pendientes', 'Carpetas con alertas'],
        ['#etiqueta-kpi-ejecucion', 'Carpetas en ejecución'],
      ]
  }
}

interface DescripcionesKpi {
  estudiantes: string
  expedientes: string
  pendientes: string
  ejecucion: string
}

function descripcionKpi(rol: IndicadoresTablero['rolPrincipal']): DescripcionesKpi {
  if (rol === 'COORDINADOR_GENERAL') {
    return {
      estudiantes: 'Estudiantes con carpeta abierta dentro del alcance institucional consultado.',
      expedientes: 'Etapas de práctica que se encuentran abiertas y requieren seguimiento activo.',
      pendientes:
        'Carpetas abiertas o pendientes de apertura con alertas: ficha de identificación incompleta, seguimiento pendiente, observación abierta, evaluaciones incompletas, periodo vencido sin cierre o etapa pendiente de apertura.',
      ejecucion: 'Carpetas abiertas que ya se encuentran en la etapa de ejecución.',
    }
  }

  if (rol === 'COORDINADOR_ASESORES') {
    return {
      estudiantes:
        'Estudiantes con asignación vigente dentro de los programas que coordina el usuario.',
      expedientes: 'Carpetas abiertas dentro del alcance de los programas coordinados.',
      pendientes:
        'Carpetas del alcance del coordinador con ficha incompleta, seguimiento pendiente, observación abierta, evaluaciones incompletas, periodo vencido sin cierre o etapa pendiente de apertura.',
      ejecucion: 'Asesores activos del programa con más de 15 estudiantes asignados vigentes.',
    }
  }

  if (rol === 'ASESOR') {
    return {
      estudiantes: 'Estudiantes con asignación vigente que requieren acompañamiento del asesor.',
      expedientes:
        'Carpetas abiertas asignadas al asesor con al menos una alerta operativa o académica.',
      pendientes:
        'Observaciones abiertas sobre estudiantes asignados que todavía requieren cierre o seguimiento.',
      ejecucion:
        'Evaluaciones requeridas todavía no enviadas en las carpetas abiertas asignadas al asesor.',
    }
  }

  if (rol === 'ESTUDIANTE') {
    return {
      estudiantes:
        'Estado actual de tu carpeta de práctica: pendiente, abierta en evaluación, abierta en ejecución o cerrada.',
      expedientes:
        'Cantidad de módulos de la carpeta que todavía requieren información o una acción pendiente.',
      pendientes:
        'Observaciones abiertas que todavía requieren tu respuesta o cierre dentro de la carpeta.',
      ejecucion:
        'Autoevaluaciones intermedia o final que todavía no han sido enviadas por el estudiante.',
    }
  }

  if (rol === 'TITULAR') {
    return {
      estudiantes: 'Estudiantes asignados al titular con carpeta abierta o pendiente de atención.',
      expedientes:
        'Registros de apoyo institucional enviados por estudiantes que todavía requieren validación del titular.',
      pendientes:
        'Evaluaciones intermedia o final que el titular aún no ha enviado para sus estudiantes asignados.',
      ejecucion:
        'Observaciones realizadas por el titular que siguen abiertas y requieren respuesta o seguimiento.',
    }
  }

  return {
    estudiantes: 'Indicador calculado según el rol y alcance autorizado.',
    expedientes: 'Carpetas visibles para el rol autenticado.',
    pendientes: 'Registros pendientes dentro del alcance autorizado.',
    ejecucion: 'Procesos visibles en etapa de ejecución.',
  }
}

function aplicarDescripcionesKpi(rol: IndicadoresTablero['rolPrincipal']): void {
  const descripciones = descripcionKpi(rol)
  const tarjetas: Array<[string, string]> = [
    ['#tarjeta-kpi-estudiantes', descripciones.estudiantes],
    ['#tarjeta-kpi-expedientes', descripciones.expedientes],
    ['#tarjeta-kpi-pendientes', descripciones.pendientes],
    ['#tarjeta-kpi-ejecucion', descripciones.ejecucion],
  ]
  tarjetas.forEach(([selector, descripcion]) => {
    const tarjeta = elemento<HTMLElement>(selector)
    tarjeta.title = descripcion
    tarjeta.setAttribute('aria-label', descripcion)
  })
}

function destruirGraficos(): void {
  graficosActivos.forEach((grafico) => {
    grafico.destroy()
  })
  graficosActivos = []
}

function opcionesBase(): ApexOptions {
  return {
    chart: {
      fontFamily: 'Nunito, system-ui, sans-serif',
      toolbar: { show: true },
      animations: { enabled: true },
      width: '100%',
      parentHeightOffset: 0,
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
    },
    colors: ['#435ebe', '#55c6e8', '#198754', '#ffc107', '#dc3545', '#6c757d'],
    dataLabels: { enabled: false },
    noData: {
      text: 'Sin información disponible',
      align: 'center',
      verticalAlign: 'middle',
      style: { color: '#7c8db5', fontSize: '14px' },
    },
    grid: {
      borderColor: '#e9ecef',
      strokeDashArray: 0,
    },
    legend: {
      position: 'bottom',
      fontFamily: 'Nunito, system-ui, sans-serif',
    },
    tooltip: {
      theme: 'light',
      y: {
        formatter: (valor) => Number(valor).toLocaleString('es-CO'),
      },
    },
  }
}

async function esperarLayoutVisible(): Promise<void> {
  await new Promise<void>((resolver) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => resolver())
    })
  })
}

async function renderizarGrafico(selector: string, opciones: ApexOptions): Promise<void> {
  const nodo = elemento<HTMLElement>(selector)
  nodo.innerHTML = ''
  const { default: ApexCharts } = await import('apexcharts')
  const grafico = new ApexCharts(nodo, opciones)
  await grafico.render()
  graficosActivos.push(grafico)
}

function altoGraficoHorizontal(categorias: number): number {
  return Math.max(320, Math.min(560, categorias * 54 + 130))
}

async function renderizarGraficos(tablero: IndicadoresTablero): Promise<void> {
  const bloqueGeneral = elemento<HTMLElement>('#bloque-graficos-coordinador-general')
  const bloqueCoordinadorAsesores = elemento<HTMLElement>('#bloque-graficos-coordinador-asesores')
  const bloqueAsesor = elemento<HTMLElement>('#bloque-graficos-asesor')
  const bloqueTitular = elemento<HTMLElement>('#bloque-graficos-titular')
  const bloqueEstudiante = elemento<HTMLElement>('#bloque-graficos-estudiante')
  destruirGraficos()
  bloqueGeneral.classList.add('d-none')
  bloqueCoordinadorAsesores.classList.add('d-none')
  bloqueAsesor.classList.add('d-none')
  bloqueTitular.classList.add('d-none')
  bloqueEstudiante.classList.add('d-none')
  if (!tablero.graficos) {
    return
  }

  if (tablero.rolPrincipal === 'COORDINADOR_GENERAL') {
    const { expedientesPorProgramaEtapa, alertasPorTipo, evaluacionesPorNivel } = tablero.graficos
    if (!expedientesPorProgramaEtapa || !alertasPorTipo || !evaluacionesPorNivel) return

    bloqueGeneral.classList.remove('d-none')
    await esperarLayoutVisible()
    await renderizarGrafico('#grafico-expedientes-programa-etapa', {
      ...opcionesBase(),
      chart: { ...opcionesBase().chart, type: 'bar', height: 360 },
      series: expedientesPorProgramaEtapa.series.map((serie) => ({
        name: serie.nombre,
        data: serie.datos,
      })),
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 4,
        },
      },
      xaxis: {
        categories: expedientesPorProgramaEtapa.categorias,
        labels: { rotate: -30, trim: true },
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        title: { text: 'Carpetas' },
        labels: { formatter: (valor) => Math.round(Number(valor)).toLocaleString('es-CO') },
      },
    })

    await renderizarGrafico('#grafico-evaluaciones-nivel', {
      ...opcionesBase(),
      chart: { ...opcionesBase().chart, type: 'donut', height: 330 },
      labels: evaluacionesPorNivel.categorias,
      series: evaluacionesPorNivel.datos,
      colors: ['#dc3545', '#ffc107', '#55c6e8', '#198754'],
      dataLabels: {
        enabled: true,
        formatter: (valor) => `${Number(valor).toFixed(0)}%`,
      },
      plotOptions: {
        pie: {
          donut: {
            size: '68%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Evaluaciones',
                formatter: () =>
                  evaluacionesPorNivel.datos
                    .reduce((total, dato) => total + dato, 0)
                    .toLocaleString('es-CO'),
              },
            },
          },
        },
      },
    })

    await renderizarGrafico('#grafico-alertas-tipo', {
      ...opcionesBase(),
      chart: { ...opcionesBase().chart, type: 'bar', height: 360 },
      series: [{ name: 'Carpetas', data: alertasPorTipo.datos }],
      colors: ['#ff7976'],
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: '55%',
        },
      },
      xaxis: {
        categories: alertasPorTipo.categorias,
        min: 0,
        title: { text: 'Carpetas' },
        labels: { formatter: (valor) => Math.round(Number(valor)).toLocaleString('es-CO') },
      },
      yaxis: {
        labels: { maxWidth: 260 },
      },
    })
    return
  }

  if (tablero.rolPrincipal === 'COORDINADOR_ASESORES') {
    const { cargaEstudiantesPorAsesor, expedientesPorAsesorEtapa, alertasPorAsesor } =
      tablero.graficos
    if (!cargaEstudiantesPorAsesor || !expedientesPorAsesorEtapa || !alertasPorAsesor) return

    bloqueCoordinadorAsesores.classList.remove('d-none')
    await esperarLayoutVisible()
    const altoAsesores = altoGraficoHorizontal(cargaEstudiantesPorAsesor.categorias.length)

    await renderizarGrafico('#grafico-carga-estudiantes-asesor', {
      ...opcionesBase(),
      chart: { ...opcionesBase().chart, type: 'bar', height: altoAsesores },
      series: [{ name: 'Estudiantes asignados', data: cargaEstudiantesPorAsesor.datos }],
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: '52%',
        },
      },
      xaxis: {
        categories: cargaEstudiantesPorAsesor.categorias,
        min: 0,
        title: { text: 'Estudiantes' },
        labels: { formatter: (valor) => Math.round(Number(valor)).toLocaleString('es-CO') },
      },
      yaxis: { labels: { maxWidth: 230 } },
    })

    await renderizarGrafico('#grafico-expedientes-asesor-etapa', {
      ...opcionesBase(),
      chart: { ...opcionesBase().chart, type: 'bar', height: altoAsesores, stacked: true },
      series: expedientesPorAsesorEtapa.series.map((serie) => ({
        name: serie.nombre,
        data: serie.datos,
      })),
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: '52%',
        },
      },
      xaxis: {
        categories: expedientesPorAsesorEtapa.categorias,
        min: 0,
        title: { text: 'Carpetas' },
        labels: { formatter: (valor) => Math.round(Number(valor)).toLocaleString('es-CO') },
      },
      yaxis: { labels: { maxWidth: 230 } },
    })

    await renderizarGrafico('#grafico-alertas-asesor', {
      ...opcionesBase(),
      chart: {
        ...opcionesBase().chart,
        type: 'bar',
        height: altoGraficoHorizontal(alertasPorAsesor.categorias.length),
        stacked: true,
      },
      series: alertasPorAsesor.series.map((serie) => ({
        name: serie.nombre,
        data: serie.datos,
      })),
      colors: ['#ff7976', '#ffc107', '#435ebe', '#55c6e8', '#198754', '#6c757d'],
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: '52%',
        },
      },
      xaxis: {
        categories: alertasPorAsesor.categorias,
        min: 0,
        title: { text: 'Alertas' },
        labels: { formatter: (valor) => Math.round(Number(valor)).toLocaleString('es-CO') },
      },
      yaxis: { labels: { maxWidth: 260 } },
    })
    return
  }

  if (tablero.rolPrincipal === 'ASESOR') {
    const { alertasPorEstudiante, evaluacionesPorEstudiante, seguimientoPendientePorTipo } =
      tablero.graficos
    if (!alertasPorEstudiante || !evaluacionesPorEstudiante || !seguimientoPendientePorTipo) return

    bloqueAsesor.classList.remove('d-none')
    await esperarLayoutVisible()
    const altoEstudiantes = altoGraficoHorizontal(alertasPorEstudiante.categorias.length)

    await renderizarGrafico('#grafico-alertas-estudiante', {
      ...opcionesBase(),
      chart: {
        ...opcionesBase().chart,
        type: 'bar',
        height: altoEstudiantes,
        stacked: true,
      },
      series: alertasPorEstudiante.series.map((serie) => ({
        name: serie.nombre,
        data: serie.datos,
      })),
      colors: ['#ff7976', '#ffc107', '#435ebe', '#55c6e8', '#6c757d'],
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: '52%',
        },
      },
      xaxis: {
        categories: alertasPorEstudiante.categorias,
        min: 0,
        title: { text: 'Alertas' },
        labels: { formatter: (valor) => Math.round(Number(valor)).toLocaleString('es-CO') },
      },
      yaxis: { labels: { maxWidth: 250 } },
    })

    await renderizarGrafico('#grafico-evaluaciones-estudiante', {
      ...opcionesBase(),
      chart: {
        ...opcionesBase().chart,
        type: 'bar',
        height: altoGraficoHorizontal(evaluacionesPorEstudiante.categorias.length),
        stacked: true,
      },
      series: evaluacionesPorEstudiante.series.map((serie) => ({
        name: serie.nombre,
        data: serie.datos,
      })),
      colors: ['#198754', '#ffc107'],
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: '52%',
        },
      },
      xaxis: {
        categories: evaluacionesPorEstudiante.categorias,
        min: 0,
        max: 6,
        tickAmount: 6,
        title: { text: 'Evaluaciones' },
        labels: { formatter: (valor) => Math.round(Number(valor)).toLocaleString('es-CO') },
      },
      yaxis: { labels: { maxWidth: 230 } },
    })

    await renderizarGrafico('#grafico-seguimiento-pendiente-tipo', {
      ...opcionesBase(),
      chart: { ...opcionesBase().chart, type: 'bar', height: 340 },
      series: [{ name: 'Pendientes', data: seguimientoPendientePorTipo.datos }],
      colors: ['#435ebe'],
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: '55%',
        },
      },
      xaxis: {
        categories: seguimientoPendientePorTipo.categorias,
        min: 0,
        title: { text: 'Registros pendientes' },
        labels: { formatter: (valor) => Math.round(Number(valor)).toLocaleString('es-CO') },
      },
      yaxis: { labels: { maxWidth: 270 } },
    })
    return
  }

  if (tablero.rolPrincipal === 'TITULAR') {
    const {
      apoyosEstadoTitular,
      evaluacionesMomentoTitular,
      observacionesAbiertasEstudianteTitular,
    } = tablero.graficos
    if (
      !apoyosEstadoTitular ||
      !evaluacionesMomentoTitular ||
      !observacionesAbiertasEstudianteTitular
    )
      return

    bloqueTitular.classList.remove('d-none')
    await esperarLayoutVisible()

    await renderizarGrafico('#grafico-apoyos-estado-titular', {
      ...opcionesBase(),
      chart: { ...opcionesBase().chart, type: 'donut', height: 330 },
      labels: apoyosEstadoTitular.categorias,
      series: apoyosEstadoTitular.datos,
      colors: ['#ffc107', '#198754', '#dc3545'],
      dataLabels: {
        enabled: true,
        formatter: (valor) => `${Number(valor).toFixed(0)}%`,
      },
      plotOptions: {
        pie: {
          donut: {
            size: '68%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Apoyos',
                formatter: () =>
                  apoyosEstadoTitular.datos
                    .reduce((total, dato) => total + dato, 0)
                    .toLocaleString('es-CO'),
              },
            },
          },
        },
      },
    })

    await renderizarGrafico('#grafico-evaluaciones-momento-titular', {
      ...opcionesBase(),
      chart: { ...opcionesBase().chart, type: 'bar', height: 330, stacked: true },
      series: evaluacionesMomentoTitular.series.map((serie) => ({
        name: serie.nombre,
        data: serie.datos,
      })),
      colors: ['#198754', '#ffc107'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '45%',
          borderRadius: 4,
        },
      },
      xaxis: {
        categories: evaluacionesMomentoTitular.categorias,
      },
      yaxis: {
        min: 0,
        title: { text: 'Evaluaciones' },
        labels: { formatter: (valor) => Math.round(Number(valor)).toLocaleString('es-CO') },
      },
    })

    await renderizarGrafico('#grafico-observaciones-estudiante-titular', {
      ...opcionesBase(),
      chart: {
        ...opcionesBase().chart,
        type: 'bar',
        height: altoGraficoHorizontal(observacionesAbiertasEstudianteTitular.categorias.length),
      },
      series: [
        { name: 'Observaciones abiertas', data: observacionesAbiertasEstudianteTitular.datos },
      ],
      colors: ['#ff7976'],
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: '52%',
        },
      },
      xaxis: {
        categories: observacionesAbiertasEstudianteTitular.categorias,
        min: 0,
        title: { text: 'Observaciones' },
        labels: { formatter: (valor) => Math.round(Number(valor)).toLocaleString('es-CO') },
      },
      yaxis: { labels: { maxWidth: 260 } },
    })
    return
  }

  if (tablero.rolPrincipal === 'ESTUDIANTE') {
    const {
      progresoModulosEstudiante,
      autoevaluacionesEstudiante,
      pendientesSeguimientoEstudiante,
    } = tablero.graficos
    if (
      !progresoModulosEstudiante ||
      !autoevaluacionesEstudiante ||
      !pendientesSeguimientoEstudiante
    )
      return

    bloqueEstudiante.classList.remove('d-none')
    await esperarLayoutVisible()

    await renderizarGrafico('#grafico-progreso-modulos-estudiante', {
      ...opcionesBase(),
      chart: { ...opcionesBase().chart, type: 'radialBar', height: 390 },
      labels: progresoModulosEstudiante.categorias,
      series: progresoModulosEstudiante.datos,
      colors: ['#435ebe', '#55c6e8', '#198754', '#ffc107', '#6f42c1', '#fd7e14', '#20c997'],
      plotOptions: {
        radialBar: {
          offsetY: 0,
          hollow: { size: '24%' },
          track: {
            background: '#eef3ff',
            strokeWidth: '92%',
          },
          dataLabels: {
            name: { fontSize: '13px' },
            value: {
              fontSize: '16px',
              formatter: (valor) => `${Number(valor).toFixed(0)}%`,
            },
            total: {
              show: true,
              label: 'Módulos completos',
              formatter: () => {
                const completos = progresoModulosEstudiante.datos.filter((dato) => dato >= 100)
                return `${completos.length}/${progresoModulosEstudiante.datos.length}`
              },
            },
          },
        },
      },
    })

    await renderizarGrafico('#grafico-autoevaluaciones-estudiante', {
      ...opcionesBase(),
      chart: { ...opcionesBase().chart, type: 'radialBar', height: 330 },
      labels: autoevaluacionesEstudiante.categorias,
      series: autoevaluacionesEstudiante.datos,
      colors: ['#435ebe'],
      plotOptions: {
        radialBar: {
          hollow: { size: '68%' },
          track: {
            background: '#eef3ff',
            strokeWidth: '100%',
          },
          dataLabels: {
            name: { fontSize: '14px' },
            value: {
              fontSize: '28px',
              fontWeight: 700,
              formatter: (valor) => `${Number(valor).toFixed(0)}%`,
            },
          },
        },
      },
    })

    await renderizarGrafico('#grafico-pendientes-seguimiento-estudiante', {
      ...opcionesBase(),
      chart: { ...opcionesBase().chart, type: 'polarArea', height: 360 },
      labels: pendientesSeguimientoEstudiante.categorias,
      series: pendientesSeguimientoEstudiante.datos,
      colors: ['#435ebe', '#55c6e8', '#ffc107', '#ff7976'],
      stroke: { colors: ['#fff'] },
      fill: { opacity: 0.85 },
      yaxis: { show: false },
      legend: {
        position: 'bottom',
        fontFamily: 'Nunito, system-ui, sans-serif',
      },
    })
  }
}

export async function cargarTablero(_roles: string[] = []): Promise<void> {
  const tablero = await consultarTablero()
  textoRol(tablero.rolPrincipal).forEach(
    ([selector, texto]) => (elemento(selector).textContent = texto),
  )
  aplicarDescripcionesKpi(tablero.rolPrincipal)
  if (tablero.rolPrincipal === 'ESTUDIANTE' && tablero.estadoExpediente) {
    elemento<HTMLElement>('#kpi-estudiantes').textContent = tablero.estadoExpediente
  } else {
    poner('#kpi-estudiantes', tablero.estudiantesActivos)
  }
  poner('#kpi-expedientes', tablero.expedientesAbiertos)
  poner('#kpi-pendientes', tablero.pendientesValidacion)
  poner('#kpi-ejecucion', tablero.enEjecucion)
  await renderizarGraficos(tablero)
  elemento('#alcance-tablero').textContent =
    tablero.alcance === 'GLOBAL'
      ? 'Alcance institucional'
      : tablero.alcance === 'PROGRAMA'
        ? 'Alcance de tu programa'
        : 'Resumen de tu práctica'
}
